/**
 * CENTRE D'ÉCARTS — le menu `ecarts` de la maquette : « Tout ce qui ne colle pas, au même endroit.
 * Un écart s'explique — il ne s'efface jamais. »
 *
 * Ce que la page SERT : les écarts DÉRIVÉS par le serveur (stock négatif, écart de bon figé, écart de
 * rétrocession côté émetteur, écart de caisse), leur justification (acte daté/attribué/motivé), la
 * correction à zéro d'un stock négatif (un MOUVEMENT écrit, jamais une réécriture), et l'export CSV du
 * relevé courant.
 *
 * ⚠️ DEUX ÉCARTS À LA MAQUETTE, statués au câblage : la source `inv` (inventaire) n'apparaît PAS — le
 * module n'existe pas, un onglet non servi n'existe pas (§23.0) ; et l'écart de RÉTROCESSION ne se
 * « marque » pas réglé ici — il se TRANCHE sur la rétrocession (arbitrage ligne à ligne qui écrit les
 * mouvements) : la modale y renvoie au lieu d'offrir un « vu » qui laisserait les lignes non arbitrées.
 */
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmptyState, Icon, KpiRow, showToast, Trans, useOverlayPanel } from '@orgazone/ui-web';
import type { IconName } from '@orgazone/ui-tokens';
import { currentCurrency, formatMoney, toInputValue } from '@orgazone/money-format';
import { api, type EcartMotif, type EcartRow, type SourceDEcart } from '../api';
import { signalerEcartsChanges } from '../compte-ecarts';
import { exporterCsv } from './export-csv';
import { key, t, type Translatable } from '../i18n';
import { readReason, reasonToSend, WRITTEN_REASONS } from '../written-reasons';

/**
 * Les sources SERVIES — vocabulaire et tons relevés de la maquette (`ECSRC`), `inv` volontairement absent.
 *
 * ⚠️ DES NOMS D'ICÔNES, PLUS DES CHEMINS RECOPIÉS (2026-09-05). Cette page dessinait ses pictogrammes
 * en SVG écrit à la main — et six d'entre eux étaient la COPIE EXACTE d'une icône du registre
 * (`receipt`, `box`, `check`, `x`, `download`, `sliders`). Une copie ne suit pas la charte : elle la
 * fige au jour où on l'a collée, et le jour où le dessin de référence est corrigé (ce qui vient
 * d'arriver à `swap`), l'écran garde l'ancien sans que rien ne le signale.
 */
const SRC: Record<SourceDEcart, { l: Translatable; ic: IconName; bg: string; fg: string }> = {
  caisse: { l: key('Caisse'), ic: 'receipt', bg: 'var(--amber-bg)', fg: 'var(--amber-fg)' },
  stockneg: { l: key('Stock négatif'), ic: 'box', bg: 'var(--rose-bg)', fg: 'var(--rose-fg)' },
  // ⚠️ `inbound` (marchandise qui ENTRE) plutôt que le carton dessiné à la main : le registre n'a pas
  //    de carton, et le sens — une réception — est ce que l'icône doit dire.
  recep: { l: key('Réception'), ic: 'inbound', bg: 'var(--blue-bg)', fg: 'var(--blue-fg)' },
  retro: { l: key('Rétrocession'), ic: 'swap', bg: 'var(--teal-100)', fg: 'var(--teal-800)' },
};

const dateCourte = (ms: number): string => {
  const d = new Date(ms);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
};
/** ± euros signés — `null` reste un tiret : une valeur inconnue se DIT, elle ne devient pas 0 €. */
const valeur = (cents: number | null): string =>
  cents == null ? '—' : (cents > 0 ? '+' : '−') + formatMoney(Math.abs(cents), { thousandsSep: ' ' });

/** Export du relevé courant — les colonnes d'ici, la mécanique partagée (`export-csv`). */
function exporterEcarts(rows: EcartRow[]) {
  exporterCsv(
    'ecarts',
    [t('Date'), t('Source'), t('Écart'), t('Détail'), t('Valeur ({v})', { v: currentCurrency().code }), t('Statut'), t('Motif'), t('Réglé par'), t('Note')],
    rows.map((r) => [
      new Date(r.date).toLocaleDateString('fr-FR'), t(SRC[r.source].l), r.label, r.sub,
      r.valueCents == null ? '' : toInputValue(r.valueCents),
      r.status === 'todo' ? t('À traiter') : t('Réglé'), r.reason && readReason(r.reason), r.by, r.note,
    ]),
  );
}

type Statut = 'todo' | 'fin' | '';

/** Une option de traitement du stock négatif — carte cliquable (jamais défini pendant un rendu, §24.1). */
function OptionNeg({ ibg, ifg, ic, heading, texte, disabled, onClick }: {
  ibg: string; ifg: string; ic: IconName; heading: string; texte: string; disabled: boolean; onClick: () => void;
}) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ display: 'flex', gap: 11, alignItems: 'flex-start', padding: '12px 14px', border: '1.5px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--paper)', textAlign: 'left', cursor: 'pointer', opacity: disabled ? 0.6 : 1 }}>
      <span style={{ width: 30, height: 30, borderRadius: 'var(--r-ctrl)', background: ibg, color: ifg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={ic} size={15} stroke={2} /></span>
      <span>
        <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)' }}>{heading}</span>
        <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--ink-600)', marginTop: 2, lineHeight: 1.5 }}>{texte}</span>
      </span>
    </button>
  );
}

// ── LA PAGE ────────────────────────────────────────────────────────────────────────────────────
export function Ecarts() {
  const [rows, setRows] = useState<EcartRow[] | null>(null);
  const [motifs, setMotifs] = useState<EcartMotif[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [statut, setStatut] = useState<Statut>('todo');
  const [source, setSource] = useState<SourceDEcart | ''>('');
  const [ouvert, setOuvert] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setFailure(null);
      const r = await api.variances();
      setRows(r.rows); setMotifs(r.motifs);
    } catch (e) { setFailure(e instanceof Error ? e.message : String(e)); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  const parStatut = useCallback((s: Statut, x: EcartRow) => !s || (s === 'todo' ? x.status === 'todo' : x.status === 'done'), []);
  const filtres = useMemo(() => (rows ?? []).filter((x) => parStatut(statut, x)).filter((x) => !source || x.source === source), [rows, statut, source, parStatut]);
  const kpis = useMemo(() => {
    const tout = rows ?? [];
    const todo = tout.filter((x) => x.status === 'todo');
    const bound = Date.now() - 30 * 24 * 3600 * 1000;
    return {
      toProcess: todo.length,
      // La valeur inconnue (null) ne compte pas : additionner des « — » inventerait un total (§23.0).
      valeur: todo.reduce((s, x) => s + Math.abs(x.valueCents ?? 0), 0),
      regles30j: tout.filter((x) => x.status === 'done' && (x.resolvedAt ?? 0) >= bound).length,
    };
  }, [rows]);

  const selection = ouvert ? (rows ?? []).find((x) => x.id === ouvert) ?? null : null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Centre d’écarts')}</h1>
          <p style={{ margin: '3px 0 0', fontSize: '13.5px', color: 'var(--ink-500)' }}>
            <Trans k='Tout ce qui ne colle pas, au même endroit. <1>Un écart s’explique — il ne s’efface jamais.</1>' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
          </p>
        </div>
        <button onClick={() => { exporterEcarts(filtres); showToast(t('{n} ligne exportée', { n: filtres.length }), 'ok'); }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 40, padding: '0 18px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer' }}>
          <Icon name="download" size={15} stroke={1.9} />{t('Export Excel')}
        </button>
      </div>

      {/* Les 3 repères — comptés sur TOUT le relevé, pas sur la vue filtrée. */}
      <KpiRow style={{ marginBottom: 12 }} items={[
        { label: t('À traiter'), value: String(kpis.toProcess), sub: t('écarts qui attendent une explication'), icon: 'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01', tone: 'amber' },
        { label: t('Valeur en attente'), value: formatMoney(kpis.valeur, { thousandsSep: ' ' }), sub: t('valeur absolue cumulée des écarts à traiter (hors valeurs inconnues)'), icon: 'M18.5 5.5A7.5 7.5 0 0 0 13 3.5a8 8 0 0 0 0 17 7.5 7.5 0 0 0 5.5-2M3.5 10h10M3.5 14h8.5', tone: 'rose' },
        { label: t('Réglés sur 30 jours'), value: String(kpis.regles30j), sub: t('justifiés ou corrigés, trace conservée'), icon: 'M5 12l5 5L20 7', tone: 'teal' },
      ]} />

      <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', overflow: 'hidden' }}>
            {/* ⚠️ « Réglés » sans « & scellés » : le statut scellé vient des inventaires, pas encore construits. */}
            {([{ v: 'todo', l: t('À traiter') }, { v: 'fin', l: t('Réglés') }, { v: '', l: t('Tous') }] as { v: Statut; l: string }[]).map((s) => (
              <button aria-pressed={statut === s.v} key={s.v} onClick={() => setStatut(s.v)} style={{
                height: 36, padding: '0 12px', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                background: statut === s.v ? 'var(--ink-100)' : 'transparent',
                color: statut === s.v ? 'var(--ink-800)' : 'var(--ink-500)', fontSize: 'var(--t-dense)', fontWeight: 700,
              }}>{s.l}</button>
            ))}
          </span>
          {([{ v: '' as const, l: t('Toutes sources') }, ...Object.entries(SRC).map(([v, d]) => ({ v: v as SourceDEcart, l: d.l }))]).map((c) => {
            const on = source === c.v;
            const n = (rows ?? []).filter((x) => parStatut(statut, x)).filter((x) => !c.v || x.source === c.v).length;
            return (
              <button aria-pressed={on} key={c.v || 'all'} onClick={() => setSource(c.v)} style={{
                height: 32, padding: '0 12px', borderRadius: 'var(--r-pill)', cursor: 'pointer', whiteSpace: 'nowrap',
                border: `1px solid ${on ? 'var(--teal-800)' : 'var(--ink-200)'}`,
                background: on ? 'var(--teal-800)' : 'var(--paper)', color: on ? '#fff' : 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 700,
              }}>{t(c.l)} <span style={{ opacity: 0.65, fontSize: '10.5px', fontWeight: 800 }}>{n}</span></button>
            );
          })}
        </div>

        {failure != null ? (
          <EmptyState icon="alert" title={t('Relevé indisponible')} note={failure} />
        ) : rows === null ? (
          <div style={{ padding: 34, textAlign: 'center', color: 'var(--ink-500)', fontSize: 'var(--t-lead)' }}>{t('Chargement…')}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 880 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '64px 132px minmax(240px,1.5fr) 104px 150px 84px', gap: 10, padding: '9px 16px', borderBottom: '1px solid var(--ink-200)', background: 'var(--ink-50)', fontSize: '10.5px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
                <div>{t('Date')}</div><div>{t('Source')}</div><div>{t('Écart')}</div><div style={{ textAlign: 'right' }}>{t('Valeur')}</div><div>{t('Statut')}</div><div />
              </div>
              {filtres.map((r) => {
                const sc = SRC[r.source];
                return (
                  <div key={r.id} style={{ display: 'grid', gridTemplateColumns: '64px 132px minmax(240px,1.5fr) 104px 150px 84px', gap: 10, padding: '9px 16px', borderBottom: '1px solid var(--ink-100)', alignItems: 'center', fontSize: '12.5px' }}>
                    <div style={{ fontSize: '11.5px', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>{dateCourte(r.date)}</div>
                    <div>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 22, padding: '0 9px', borderRadius: 'var(--r-xs)', background: sc.bg, color: sc.fg, fontSize: '10.5px', fontWeight: 800, whiteSpace: 'nowrap' }}>
                        <Icon name={sc.ic} size={11} stroke={2.1} />{t(sc.l)}
                      </span>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.label}</div>
                      <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.sub}</div>
                    </div>
                    <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 800, color: (r.valueCents ?? 0) > 0 ? 'var(--teal-800)' : 'var(--rose-fg)' }}
                      title={r.valueCents == null ? t('Valeur inconnue — aucun coût défendable pour cet article') : undefined}>{valeur(r.valueCents)}</div>
                    <div>
                      <span style={{ height: 22, padding: '0 9px', borderRadius: 'var(--r-xs)', background: r.status === 'todo' ? 'var(--amber-bg)' : 'var(--teal-100)', color: r.status === 'todo' ? 'var(--amber-fg)' : 'var(--teal-800)', fontSize: '10.5px', fontWeight: 800, display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                        {r.status === 'todo' ? t('À traiter') : t('Réglé')}
                      </span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <button onClick={() => setOuvert(r.id)} style={{ height: 30, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '11.5px', fontWeight: 700, cursor: 'pointer' }}>
                        {r.status === 'todo' ? t('Traiter') : t('Voir')}
                      </button>
                    </div>
                  </div>
                );
              })}
              {filtres.length === 0 ? (
                <div style={{ padding: 34, textAlign: 'center' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 'var(--r-sm)', background: 'var(--teal-100)', color: 'var(--teal-800)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 9px' }}><Icon name="check" size={18} stroke={2.2} /></div>
                  <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Rien dans cette vue')}</div>
                  <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 3 }}>{t('Tout est expliqué — c’est exactement l’objectif.')}</div>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
      <div style={{ marginTop: 9, fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>{t('Chaque règlement est tracé — qui, quand, motif. Rien ne s’efface.')}</div>

      {selection ? (
        <ModaleEcart variance={selection} motifs={motifs} onClose={() => setOuvert(null)}
          // ⚠️ Le rail porte la pastille du nombre d'écarts ouverts : sans ce signal, elle resterait
          //    sur le chiffre d'avant — un « 3 » collé au menu alors qu'il n'en reste plus (§23.0).
          onFait={() => { setOuvert(null); void reload(); signalerEcartsChanges(); }} />
      ) : null}
    </div>
  );
}

// ── LA MODALE DE TRAITEMENT — un corps par source, le geste que le serveur accepte ─────────────────
function ModaleEcart({ variance, motifs, onClose, onFait }: {
  variance: EcartRow; motifs: EcartMotif[]; onClose: () => void; onFait: () => void;
}) {
  const nav = useNavigate();
  const [reason, setMotif] = useState('');
  const [note, setNote] = useState('');
  const [liveCount, setEnCours] = useState(false);
  const sc = SRC[variance.source];
  const todo = variance.status === 'todo';
  // La caisse impose un motif du RÉFÉRENTIEL (portée cash_variance) — même liste que la journée de caisse.
  const motifsCaisse = motifs.filter((m) => m.scope === 'cash_variance');

  const agir = async (geste: () => Promise<unknown>, toast: string) => {
    setEnCours(true);
    try {
      await geste();
      showToast(toast, 'ok');
      onFait();
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
      setEnCours(false);
    }
  };

  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  const { panelRef } = useOverlayPanel({ open: true, onClose });
  const titleId = useId();
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(12,14,16,.45)' }} />
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} style={{ position: 'relative', width: 560, maxWidth: '100%', maxHeight: 'calc(100vh - 40px)', background: 'var(--paper)', borderRadius: 'var(--r-md)', boxShadow: '0 24px 70px rgba(10,12,14,.35)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '15px 20px', borderBottom: '1px solid var(--ink-200)' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 22, padding: '0 9px', borderRadius: 'var(--r-xs)', background: sc.bg, color: sc.fg, fontSize: '10.5px', fontWeight: 800 }}><Icon name={sc.ic} size={11} stroke={2.1} />{t(sc.l)}</span>
          <span id={titleId} style={{ fontSize: 'var(--t-control)', fontWeight: 700, color: 'var(--ink-900)', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{variance.id} · {dateCourte(variance.date)}</span>
          <button onClick={onClose} aria-label={t('Fermer')} style={{ width: 32, height: 32, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }}><Icon name="x" size={15} stroke={2.2} /></button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 15px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', marginBottom: 12 }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)' }}>{variance.label}</span>
              <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--ink-500)', marginTop: 2 }}>{variance.sub}</span>
            </span>
            <span style={{ fontSize: 'var(--t-h3)', fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: (variance.valueCents ?? 0) > 0 ? 'var(--teal-800)' : 'var(--rose-fg)', whiteSpace: 'nowrap' }}>{valeur(variance.valueCents)}</span>
          </div>

          {variance.status === 'done' ? (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 'var(--r-sm)', background: 'var(--teal-100)' }}>
              <span style={{ display: 'flex', color: 'var(--teal-800)', paddingTop: 1 }}><Icon name="check" size={15} stroke={2.2} /></span>
              <span style={{ fontSize: '12.5px', color: 'var(--teal-800)', lineHeight: 1.55 }}>
                <strong>{variance.resolvedAt
                  ? t('Réglé par {qui} · {date}', { qui: variance.by ?? '—', date: dateCourte(variance.resolvedAt) })
                  : t('Réglé par {qui}', { qui: variance.by ?? '—' })}</strong><br />
                {variance.reason ? readReason(variance.reason) : ''}{variance.note ? ` — « ${variance.note} »` : ''}
              </span>
            </div>
          ) : null}

          {todo && variance.source === 'caisse' ? (
            <div>
              <div style={{ fontSize: 'var(--t-meta)', fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 7 }}>{t('Motif — liste fermée')}</div>
              {motifsCaisse.length === 0 ? (
                // Référentiel vide : on le DIT — un menu en dur ferait diverger l'écran du référentiel.
                <div style={{ fontSize: '12.5px', color: 'var(--ink-500)', marginBottom: 10, lineHeight: 1.5 }}>
                  <Trans k='Aucun motif de portée <1>cash_variance</1> au référentiel — ajoutez-en dans Référentiels avant de justifier.' parts={[(x) => <code style={{ fontFamily: 'var(--font-mono)' }}>{x}</code>]} /></div>
              ) : (
                <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 10 }}>
                  {motifsCaisse.map((m) => {
                    const on = reason === m.code;
                    return (
                      <button key={m.code} onClick={() => setMotif(m.code)} style={{ height: 36, padding: '0 13px', border: `1.5px solid ${on ? 'var(--teal-800)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-pill)', background: on ? 'var(--teal-800)' : 'var(--paper)', color: on ? '#fff' : 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 700, cursor: 'pointer' }}>{m.label}</button>
                    );
                  })}
                </div>
              )}
              <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('Note (facultatif) — ce que vous avez constaté…')}
                style={{ width: '100%', boxSizing: 'border-box', height: 64, padding: '9px 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', fontSize: '12.5px', fontFamily: 'inherit', outline: 'none', resize: 'none', background: 'var(--paper)', color: 'var(--ink-900)' }} />
            </div>
          ) : null}

          {todo && variance.source === 'stockneg' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <OptionNeg ibg="var(--teal-100)" ifg="var(--teal-800)" disabled={liveCount}
                ic="sliders"
                heading={t('Corriger le stock à zéro')}
                texte={t('Écrit un mouvement de correction signé — la pièce n’existe pas, le stock doit le dire.')}
                onClick={() => void agir(() => api.fixNegativeStock(variance.id), t('Mouvement de correction écrit — stock à zéro'))} />
              <OptionNeg ibg="var(--amber-bg)" ifg="var(--amber-fg)" disabled={liveCount}
                ic="clock"
                heading={t('Laisser négatif — réception attendue')}
                texte={t('La pièce a été vendue avant l’entrée de son BL. Le stock se réalignera à l’intégration.')}
                onClick={() => void agir(() => api.justifyVariance(variance.id, reasonToSend(WRITTEN_REASONS.negativeAwaitingReceipt), null), t('Écart justifié — trace conservée'))} />
            </div>
          ) : null}

          {todo && variance.source === 'recep' ? (
            <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('Note (facultatif) — accord trouvé, avoir demandé…')}
              style={{ width: '100%', boxSizing: 'border-box', height: 64, padding: '9px 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', fontSize: '12.5px', fontFamily: 'inherit', outline: 'none', resize: 'none', background: 'var(--paper)', color: 'var(--ink-900)' }} />
          ) : null}

          {todo && variance.source === 'retro' ? (
            // ⚠️ Décision de câblage : pas de « Marquer réglé — vu entre magasins » ici. L'écart de
            // rétrocession se TRANCHE ligne à ligne (jamais partie / perdue en transit) — c'est
            // l'arbitrage qui écrit les mouvements ; un simple « vu » laisserait les pièces en limbes.
            <div style={{ fontSize: '12.5px', color: 'var(--ink-600)', lineHeight: 1.55 }}>
              <Trans k='L’écart d’une rétrocession se tranche sur la rétrocession elle-même — <1>jamais partie</1> (retour au stock) ou <2>perdue en transit</2> (démarque). C’est l’arbitrage qui écrit les mouvements.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
          ) : null}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 20px', borderTop: '1px solid var(--ink-200)', background: 'var(--ink-50)' }}>
          <span style={{ fontSize: '11.5px', color: 'var(--ink-500)', flex: 1 }}>
            {todo ? t('Le règlement est tracé — qui, quand, motif. Rien ne s’efface.') : t('Trace conservée au journal.')}
          </span>
          {todo && variance.source === 'caisse' ? (
            <button disabled={!reason || liveCount} onClick={() => void agir(() => api.justifyVariance(variance.id, reason, note.trim() || null), t('Écart justifié — trace conservée'))}
              style={{ height: 38, padding: '0 16px', border: 'none', borderRadius: 'var(--r-ctrl)', background: reason ? 'var(--teal-800)' : 'var(--ink-300)', color: '#fff', fontSize: 'var(--t-lead)', fontWeight: 700, cursor: reason ? 'pointer' : 'default' }}>{t('Justifier l’écart')}</button>
          ) : null}
          {todo && variance.source === 'recep' ? (
            <button disabled={liveCount} onClick={() => void agir(() => api.justifyVariance(variance.id, reasonToSend(WRITTEN_REASONS.seenWithSupplier), note.trim() || null), t('Écart réglé — vu avec le fournisseur'))}
              style={{ height: 38, padding: '0 16px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-800)', color: '#fff', fontSize: 'var(--t-lead)', fontWeight: 700, cursor: 'pointer' }}>{t('Marquer réglé — vu avec le fournisseur')}</button>
          ) : null}
          {todo && variance.source === 'retro' && variance.retroId ? (
            <button onClick={() => nav(`/retrocessions?open=${encodeURIComponent(variance.retroId!)}`)}
              style={{ height: 38, padding: '0 16px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-900)', color: 'var(--paper)', fontSize: 'var(--t-lead)', fontWeight: 700, cursor: 'pointer' }}>{t('Trancher sur la rétrocession')}</button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
